import React, { useMemo, useLayoutEffect, useState } from 'react';
import { useStandardCollator } from 'js/utils/string';
import { getAnswerDomain } from 'js/utils/scoring';
import { scaleColorTraffic } from 'js/utils/color';

import { useParentSize } from '@visx/responsive';
import { useId } from 'js/hooks/useId';
import { Icon } from 'js/components';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';

import AnswerSetSelector from './AnswerSetSelector';

import ScoredChoice from './Choices/ScoredChoice';
import { useAnswerSetContext } from './AnswerSetContext';

import styles from './AnswerSet.module.scss';

export function ScoredChoiceEditor({
  answerSet,
  questionId,
  questionType,
  onAddChoice = () => {},
  onDeleteChoice = () => {},
  onAnswerSetChange = () => {},
  onChoiceUpdate = () => {},
  idAttribute = 'guid',
}) {
  const collator = useStandardCollator();
  const [nthChildOffset, setNthChildOffset] = useState(null);
  const { parentRef, width } = useParentSize({ debounceTime: 0 });
  const { isValidAnswerSetTypeForQuestion } = useAnswerSetContext();
  const id = useId();

  const choices = useMemo(() => {
    return !answerSet?.choices ||
      !isValidAnswerSetTypeForQuestion(questionType, answerSet?.type)
      ? []
      : Object.values(answerSet.choices).sort((a, b) =>
          collator.compare(a.p, b.p)
        );
  }, [
    answerSet?.choices,
    answerSet?.type,
    questionType,
    collator,
    isValidAnswerSetTypeForQuestion,
  ]);

  // const choices = useMemo(() => {
  //   return !answerSet?.choices
  //     ? []
  //     : Object.values(answerSet.choices).sort((a, b) =>
  //         collator.compare(a.p, b.p)
  //       );
  // }, [answerSet?.choices, collator]);

  useLayoutEffect(() => {
    const parentElement = parentRef.current;
    let previousOffset = null;

    if (parentElement) {
      const flexChildren = Array.from(parentElement.children);
      const newRowIndex = flexChildren.findIndex((flexChild, index) => {
        previousOffset = previousOffset ?? flexChild.offsetTop;
        return flexChild.offsetTop > previousOffset;
      });

      if (newRowIndex > 0) {
        setNthChildOffset(newRowIndex);
      } else {
        setNthChildOffset(null);
      }
    } else {
      setNthChildOffset(null);
    }
  }, [parentRef, width, choices.length]);

  const handleAddChoice = () => {
    const nextNumber = choices.length + 1;

    const newChoice = {
      value: '' + nextNumber,
      text: `Choice ${nextNumber}`,
    };
    onAddChoice(newChoice);
  };

  const handleChoiceUpdate = (choiceId, update) => {
    onChoiceUpdate(answerSet[idAttribute], choiceId, update);
  };

  const handleChoiceDelete = (choiceId) => {
    onDeleteChoice(answerSet[idAttribute], choiceId);
  };

  const color = scaleColorTraffic
    .copy()
    .domain(getAnswerDomain(choices, 'value', 3));

  return (
    <>
      <style>
        {`#${id} .${styles.choice}:nth-child(${nthChildOffset}n+1)::before { display: none !important; }`}
      </style>
      <div
        className={`${styles['choice-list-null']} ${styles['choice-list']}`}
        ref={parentRef}
        id={id}
      >
        {choices.map((choice, index) => (
          <ScoredChoice
            key={choice.guid ?? choice.id}
            choice={choice}
            index={index}
            answerSetId={answerSet[idAttribute]}
            questionId={questionId}
            color={color}
            onUpdate={handleChoiceUpdate}
            onDelete={handleChoiceDelete}
          />
        ))}
        <div className={styles['add-choice']}>
          <button className="btn btn-light" onClick={handleAddChoice}>
            <Icon icon={faCirclePlus} className="icon" size="lg" />
            <span className={styles['add-choice-text']}>Add choice</span>
          </button>
        </div>
        {choices.length === 0 && (
          <>
            <div className={styles['choose-answer-set-or']}>OR</div>
            <div className={styles['choose-answer-set-inline']}>
              <p>Choose an existing answer set</p>
              <div>
                <AnswerSetSelector
                  currentAnswerSet={answerSet}
                  questionType={questionType}
                  onChange={onAnswerSetChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ScoredChoiceEditor;
